import { lazy, useState } from "react";
import { Typography } from "@mui/material";
import PropTypes from "prop-types";

import { useUserData } from "src/context/userContext";
import { person_fetch_by_id } from "src/data/person/PersonFunctions";
import { asiakas_fetch } from "src/data/asiakas/AsiakasFunctions";
import { tyomaa_fetch } from "src/data/tyomaa/tyomaaFunctions";

import LazyLoader from "src/components/layout/Progress/LazyLoader";
import Attachments_search from "src/pages/attachments/components/Attachments_search";

const EditTyomaaModal = lazy(() => import("src/components/tyomaa/edit/EditTyomaaModal"));
const EditPersonModal = lazy(() => import("src/components/person/editPerson/EditPersonModal"));

const PersonSelectorSearch = lazy(() =>
  import("src/components/person/PersonSelectorSearch/PersonSelectorSearch")
);
const AsiakasSelectorSearch = lazy(() =>
  import("src/components/asiakas/AsiakasSelectorSearch/AsiakasSelectorSearch")
);
const TyomaaSelectorSearch = lazy(() =>
  import("src/components/tyomaa/TyomaaSelectorSearch/TyomaaSelectorSearch")
);
const KeikkaSelectorSearch = lazy(() =>
  import("src/data/keikka/KeikkaSelectorSearch/KeikkaSelectorSearch")
);
const EditAsiakas = lazy(() => import("src/components/asiakas/edit/EditAsiakas"));

const styles = {
  row: { width: "500px", maxWidth: "90vw", padding: "20px" },
};

export default function GlobalSearchModalContents(props) {
  const { token, user, userAsiakas } = useUserData();
  const ownerAsiakasId = userAsiakas?.asiakasId;
  const onClose = props.onClose;

  // eslint-disable-next-line no-unused-vars
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [selectedAsiakas, setSelectedAsiakas] = useState(null);
  const [selectedTyomaa, setSelectedTyomaa] = useState(null);
  const [editPersonIsOpen, setEditPersonIsOpen] = useState(false);
  const [editAsiakasIsOpen, setEditAsiakasIsOpen] = useState(false);
  const [editTyomaaIsOpen, setEditTyomaaIsOpen] = useState(false);
  const handleEditPersonChange = () => {
    setEditPersonIsOpen(false);
  };
  const handleEditPersonClose = () => {
    setEditPersonIsOpen(false);
    setSelectedPerson(null);
  };

  const doSetSelectedPersonId = async (personId) => {
    setIsLoading(true);
    const person = await person_fetch_by_id(personId, token);
    setSelectedPerson(person);
    setEditPersonIsOpen(true);
    setIsLoading(false);
  };
  const doSetSelectedAsiakasId = async (asiakasId) => {
    setIsLoading(true);
    const asiakas = await asiakas_fetch(user, asiakasId, token, 0, ownerAsiakasId);
    setSelectedAsiakas(asiakas.globalAsiakas);
    setEditAsiakasIsOpen(true);
    setIsLoading(false);
  };
  const doSetSelectedTyomaaId = async (tyomaaId) => {
    setIsLoading(true);
    const tyomaa = await tyomaa_fetch(user.personId, tyomaaId, token, null);
    setSelectedTyomaa(tyomaa.globalTyomaa);
    setEditTyomaaIsOpen(true);
    setIsLoading(false);
  };
  // const doSetSelectedKeikkaId = async (keikkaId) => {
  //   setIsLoading(true);
  //   const keikka = await keikka_fetch(keikkaId, user, ownerAsiakasId, token);
  //   setSelectedKeikka(keikka);
  //   setEditKeikkaIsOpen(true);
  //   setIsLoading(false);
  // };

  const handleEditAsiakasCancel = () => {
    setEditAsiakasIsOpen(false);
  };
  const handleEditAsiakasSave = () => {
    setEditAsiakasIsOpen(false);
  };
  const handleEditTyomaaCancel = () => {
    setEditTyomaaIsOpen(false);
  };
  const handleEditTyomaaSave = () => {
    setEditTyomaaIsOpen(false);
  };

  if (!ownerAsiakasId)
    return (
      <>
        <Typography variant="h6" color="primary">
          Ei asiakasId omistajaa
        </Typography>
      </>
    );
  return (
    <>
      <LazyLoader>
        <EditPersonModal
          personId={Number(selectedPerson?.personId)}
          isOpen={editPersonIsOpen}
          onClose={handleEditPersonClose}
          onChange={handleEditPersonChange}
          asiakasId={ownerAsiakasId}
        />
        <EditAsiakas
          isOpen={editAsiakasIsOpen}
          asiakasId={selectedAsiakas?.asiakasId}
          onCancel={handleEditAsiakasCancel}
          onSave={handleEditAsiakasSave}
          editKeikkaAsiakas={false}
        />
        <EditTyomaaModal
          isOpen={editTyomaaIsOpen}
          tyomaaId={selectedTyomaa?.tyomaaId}
          onClose={handleEditTyomaaCancel}
          onSave={handleEditTyomaaSave}
          keikkaData={null}
          editKeikkaTyomaa={false}
        />
      </LazyLoader>
      <div style={styles.row}>
        <Typography variant="h6" color="primary">
          Henkilö
        </Typography>
        <Typography variant="body" color="primary">
          Hae henkilö sähköpostiosoitteella, nimellä tai puhelinnumerolla
        </Typography>
        <LazyLoader>
          <PersonSelectorSearch
            setPersonId={doSetSelectedPersonId}
            defaultPersonId={null}
            showEditButton
          />
        </LazyLoader>
      </div>
      <div style={styles.row}>
        <Typography variant="h6" color="primary">
          Asiakas
        </Typography>
        <Typography variant="body" color="primary">
          Hae asiakas sähköpostiosoitteella, nimellä, puhelinnumerolla tai y-tunnuksella
        </Typography>
        <LazyLoader>
          <AsiakasSelectorSearch
            setAsiakasId={doSetSelectedAsiakasId}
            defaultAsiakasId={null}
            showEditButton
            variant="standard"
            shrinkLabel
          />
        </LazyLoader>
      </div>
      <div style={styles.row}>
        <Typography variant="h6" color="primary">
          Työmaa
        </Typography>
        <Typography variant="body" color="primary">
          Hae työmaa sähköpostiosoitteella, nimellä, puhelinnumerolla tai osoitteella
        </Typography>
        <LazyLoader>
          <TyomaaSelectorSearch
            setTyomaaId={doSetSelectedTyomaaId}
            defaultTyomaaId={null}
            showEditButton
            keikkaId={null}
            shrinkLabel
          />
        </LazyLoader>
      </div>
      <div style={styles.row}>
        <Typography variant="h6" color="primary">
          Tilaus
        </Typography>
        <Typography variant="body" color="primary">
          Hae tilaus sähköpostiosoitteella, nimellä, puhelinnumerolla, y-tunnuksella, osoitteella
          tai muulla tiedolla
        </Typography>
        <LazyLoader>
          <KeikkaSelectorSearch
            showEditButton
            onClose={onClose}
            ownerAsiakasId={userAsiakas.asiakasId}
          />
        </LazyLoader>
      </div>

      <div style={styles.row}>
        <Typography variant="h6" color="primary">
          Liitetiedosto
        </Typography>
        <Typography variant="body" color="primary">
          Hae liitetiedostoja lisätietojen perusteella
        </Typography>
        <LazyLoader>
          <Attachments_search />
        </LazyLoader>
      </div>
    </>
  );
}

GlobalSearchModalContents.propTypes = {
  onClose: PropTypes.func,
};
